import { AxiosPromise } from 'axios';
import { http } from 'services';
import config from 'config';
import * as Types from './types';

export const Preview = (testId: string): AxiosPromise<unknown> =>
  http.request.get(`/api/tests/${testId}/preview`);

export const StartInfo = (
  testId: string,
  inviteCode?: string,
): AxiosPromise<Types.IApi.StartInfo.Response> =>
  http.request.get(`/api/attempts/start-info/${testId}`, {
    params: inviteCode ? { invite: inviteCode } : undefined,
  });

export const Start = (data: Types.IApi.Start.Request): AxiosPromise<Types.IApi.Start.Response> =>
  http.request.post('/api/attempts/start', data);

// --- No-account guest flow (public test link) ------------------------------

export const GuestInfo = (token: string): AxiosPromise<Types.IApi.GuestInfo.Response> =>
  http.request.get(`/api/attempts/guest/info/${token}`);

export const GuestStart = (
  data: Types.IApi.GuestStart.Request,
): AxiosPromise<Types.IApi.GuestStart.Response> =>
  http.request.post('/api/attempts/guest/start', data);

// Resumes the session bound to the guest token (sent as Bearer by the interceptor).
export const GuestSession = (): AxiosPromise<Types.IApi.Start.Response> =>
  http.request.get('/api/attempts/guest/session');

export const GuestResult = (resultToken: string): AxiosPromise<Types.IApi.GuestResult.Response> =>
  http.request.get(`/api/attempts/guest/result/${resultToken}`);

export const RequestReview = (resultToken: string): AxiosPromise<{ requested: true }> =>
  http.request.post(`/api/attempts/guest/result/${resultToken}/request-review`);

// Direct download URL for the result-summary PDF (anchor href / window.open).
export const guestResultPdfUrl = (resultToken: string): string =>
  `${config.api.base_url}/api/attempts/guest/result/${resultToken}/pdf`;

export const SaveAnswer = (
  attemptId: string,
  data: Types.IApi.SaveAnswer.Request
): AxiosPromise<Types.IApi.SaveAnswer.Response> =>
  http.request.post(`/api/attempts/${attemptId}/answer`, data);

export const SaveAnswersBatch = (
  attemptId: string,
  answers: Types.IApi.SaveAnswer.BatchItem[]
): AxiosPromise<{ saved: number }> =>
  http.request.post(`/api/attempts/${attemptId}/answers`, { answers });

export const LogEvent = (
  attemptId: string,
  data: Types.IApi.LogEvent.Request
): AxiosPromise<Types.IApi.LogEvent.Response> =>
  http.request.post(`/api/attempts/${attemptId}/event`, data);

export const Submit = (attemptId: string): AxiosPromise<Types.IApi.Submit.Response> =>
  http.request.post(`/api/attempts/${attemptId}/submit`);

export const Single = ({ id }: Types.IApi.Single.Params): AxiosPromise<Types.IApi.Single.Response> =>
  http.request.get(`/api/attempts/${id}`);

export const ByTest = ({ testId }: Types.IApi.ByTest.Params): AxiosPromise<Types.IApi.ByTest.Response[]> =>
  http.request.get(`/api/attempts/test/${testId}`);

export interface ActiveAttemptItem {
  id: string;
  testId: string;
  testTitle: string;
  candidate: { id: string | null; name: string; email: string; isGuest?: boolean } | null;
  startedAt: string;
  progress: number;
  answerCount: number;
  totalQuestions: number;
  timeElapsed: number;
  violations: number;
  warnings: number;
  status: 'active' | 'warning' | 'violation';
}

/** Server-computed monitoring header counters (span ALL attempt statuses). */
export interface MonitoringSummary {
  inProgress: number;
  /** Attempts with status SUBMITTED / GRADING / GRADED / EXPIRED. */
  submitted: number;
  /** Distinct attempts (any status) with at least one violation event. */
  flagged: number;
  /** Effective seconds left in the test session; null when untimed / no deadline. */
  timeLeftSec: number | null;
}

export interface MonitoringEventItem {
  id: string | number;
  attemptId: string;
  candidateName?: string;
  kind: 'started' | 'violation' | 'submitted';
  type?: string;
  at: number;
}

export const Active = (
  params?: { testId?: string },
): AxiosPromise<{ items: ActiveAttemptItem[]; events?: MonitoringEventItem[]; summary: MonitoringSummary }> =>
  http.request.get('/api/attempts/active', { params });
